/** Shared marketing CMS shapes — keep in sync with Studio schemas + seed. */

export const MARKETING_THEME_PRESETS = [
  "editorial",
  "goldForward",
  "wineForward",
  "midnightCool",
  "paperLight",
] as const;

export type MarketingThemePreset = (typeof MARKETING_THEME_PRESETS)[number];

export type SanityImageRef = {
  asset?: { _ref?: string; _id?: string; url?: string } | null;
  alt?: string;
} | null;

export type MarketingNavData = {
  wordmark: string;
  logoAlt: string;
  ctaLabel: string;
  homeLinks: { night: string; features: string; venues: string; about: string };
  aboutLinks: { home: string; features: string; venues: string; about: string };
};

export type MarketingFooterData = {
  wordmark: string;
  aboutLabel: string;
  vendorLabel: string;
  loginLabel: string;
};

export type MarketingWaitlistCopy = {
  placeholder: string;
  ariaLabel: string;
  consentLabel: string;
  consentError: string;
  successMessage: string;
  genericError: string;
  loadingLabel: string;
  liveCountTemplate: string;
};

export type MarketingSeo = {
  title: string;
  description: string;
};

export type FeatureMockupCopy = {
  radarLabel: string;
  chatUser: string;
  chatAi: string;
  chatChips: string[];
  listRows: Array<{ name: string; status: string; statusTone?: "on" | "soon" }>;
};

export type LandingPageData = {
  themePreset: MarketingThemePreset;
  brand: { logo: SanityImageRef; logoUrl?: string };
  seo: MarketingSeo;
  nav: MarketingNavData;
  footer: MarketingFooterData;
  waitlist: MarketingWaitlistCopy;
  hero: {
    eyebrow: string;
    headline: string;
    headlineMiddle: string;
    headlineHighlight: string;
    leadText: string;
    ctaLabel: string;
    microcopy: string;
    visualLabel: string;
    visualBackground: SanityImageRef;
    visualBackgroundUrl?: string;
    openVenuesLabel: string;
    curatedVenuesLabel: string;
  };
  nightSection: { kicker: string; headline: string };
  featuresSection: { kicker: string; headline: string };
  venuesSection: { label: string };
  outlookSection: { kicker: string; headline: string };
  timelineSteps: Array<{ stepNumber: string; title: string; body: string }>;
  featureCards: Array<{
    tag: string;
    title: string;
    body: string;
    mockupType: "radar" | "chat" | "list";
  }>;
  featureMockups: FeatureMockupCopy;
  venueCategories: string[];
  outlookCards: Array<{ badge: string; title: string; body: string }>;
  finalCta: { headline: string; subtext: string };
};

export type AboutPageData = {
  themePreset: MarketingThemePreset;
  brand: { logo: SanityImageRef; logoUrl?: string };
  seo: MarketingSeo;
  nav: MarketingNavData;
  footer: MarketingFooterData;
  manifesto: {
    eyebrow: string;
    headlineLine1: string;
    headlineLine2Prefix: string;
    headlineHighlight: string;
    sub: string;
    logo: SanityImageRef;
    logoUrl?: string;
    logoAlt: string;
  };
  comparison: {
    kicker: string;
    headline: string;
    intro: string;
    oldTagline: string;
    oldTags: string[];
    newTagline: string;
    newStatement: string;
  };
  vision: {
    kicker: string;
    headlineLine1: string;
    headlineLine2: string;
    paragraphs: string[];
    expansionStages: string[];
    stageSeparator: string;
  };
  focus: {
    kicker: string;
    body: string;
    mark: SanityImageRef;
    markUrl?: string;
  };
  community: { headline: string; body: string; ctaLabel: string };
};

const DEFAULT_NAV: MarketingNavData = {
  wordmark: "Pulse",
  logoAlt: "Pulse Logo",
  ctaLabel: "Warteliste",
  homeLinks: {
    night: "Deine Nacht",
    features: "Features",
    venues: "Venues",
    about: "Über uns",
  },
  aboutLinks: {
    home: "Startseite",
    features: "Features",
    venues: "Venues",
    about: "Über uns",
  },
};

const DEFAULT_FOOTER: MarketingFooterData = {
  wordmark: "Pulse",
  aboutLabel: "Über uns",
  vendorLabel: "Venue Portal",
  loginLabel: "Login",
};

const DEFAULT_WAITLIST: MarketingWaitlistCopy = {
  placeholder: "deine@email.ch",
  ariaLabel: "E-Mail für die Warteliste",
  consentLabel: "Ich bin einverstanden, dass Pulse mir eine Einladung sendet.",
  consentError: "Bitte stimme der Einladungs-Mail zu.",
  successMessage: "Du bist drauf — teile deinen Link:",
  genericError: "Anmeldung fehlgeschlagen",
  loadingLabel: "…",
  liveCountTemplate: "{count} already on the list",
};

/** Fallback when Sanity is empty / unreachable — copy from landing mockup. */
export const DEFAULT_LANDING_PAGE: LandingPageData = {
  themePreset: "editorial",
  brand: { logo: null },
  seo: {
    title: "Pulse — Der Puls der Stadt",
    description: "Kuratierte Nightlife-App für Zürich. Warteliste sichern.",
  },
  nav: DEFAULT_NAV,
  footer: DEFAULT_FOOTER,
  waitlist: DEFAULT_WAITLIST,
  hero: {
    eyebrow: "ZÜRICH · BALD LIVE",
    headline: "Am Puls deiner Stadt.",
    headlineMiddle: "Jede Nacht",
    headlineHighlight: "neu.",
    leadText:
      "Spür den Vibe deiner Stadt — kuratierte Venues statt endlosem Scrollen. Dein Abend, in Echtzeit mit deinen Freunden geplant.",
    ctaLabel: "Warteliste sichern",
    microcopy: "Invite 2 friends — when both activate, you get 2 months of Pulse Elite.",
    visualLabel: "LIVE PULS · ZÜRICH",
    visualBackground: null,
    openVenuesLabel: "Venues gerade geöffnet",
    curatedVenuesLabel: "kuratierte Venues",
  },
  nightSection: {
    kicker: "EIN ABEND MIT PULSE",
    headline: "So verläuft deine Nacht.",
  },
  featuresSection: {
    kicker: "FEATURES",
    headline: "Weniger scrollen, mehr erleben.",
  },
  venuesSection: {
    label: "KURATIERT FÜR ZÜRICHS NÄCHTE",
  },
  outlookSection: {
    kicker: "AUSBLICK",
    headline: "Mehr kommt, sobald Zürich pulsiert.",
  },
  timelineSteps: [
    {
      stepNumber: "01",
      title: "Kuratierte Events & Venues entdecken",
      body: "Sieh live, was heute in Zürich läuft — und entdecke über 200 Bars, Clubs und Rooftops in der Schweiz, kuratiert nach deinem Stil.",
    },
    {
      stepNumber: "02",
      title: "Unentschlossen?",
      body: "We got you covered! Frag einfach deinen persönlichen AI Concierge: Basierend auf deiner Stimmung und deinen Präferenzen stellt er dir deinen perfekten Abend zusammen.",
    },
    {
      stepNumber: "03",
      title: "Memories",
      body: "Erlebe deine Abende erneut. Halte Fotos und Erinnerungen sicher an einem Ort fest und teile eure unvergesslichen Nächte mit euren Freunden.",
    },
  ],
  featureCards: [
    {
      tag: "LIVE CIRCLES",
      title: "Nie mehr „wo seid ihr?“",
      body: "Sieh live, wo dein Circle gerade ist — ohne zehn ungelesene Chat-Nachrichten.",
      mockupType: "radar",
    },
    {
      tag: "AI NIGHT CONCIERGE",
      title: "Kein „wohin sollen wir“ mehr.",
      body: "Aus über 200 Bars, Clubs und Rooftops stellt er dir basierend auf Stimmung und Vorlieben den passenden Abend zusammen.",
      mockupType: "chat",
    },
    {
      tag: "LIVE MAP",
      title: "Was JETZT offen hat.",
      body: "Kein Rätselraten und kein ewiges Google-Maps-Wischen — die Karte zeigt, was gerade läuft und was bald öffnet.",
      mockupType: "list",
    },
  ],
  featureMockups: {
    radarLabel: "5 FREUNDE LIVE",
    chatUser: "Date Night, eher ruhig, zu zweit",
    chatAi: "3 Vorschläge für euch — passend zur Stimmung",
    chatChips: ["Rooftop", "Ruhig", "Cocktails"],
    listRows: [
      { name: "Rimini Bar", status: "OFFEN", statusTone: "on" },
      { name: "Zukunft", status: "23:00", statusTone: "soon" },
      { name: "Hive", status: "OFFEN", statusTone: "on" },
    ],
  },
  venueCategories: [
    "Rooftop Bars",
    "Underground Clubs",
    "Cocktail-Adressen",
    "Live Music Venues",
    "Afterhour Spots",
    "Hidden Gems",
  ],
  outlookCards: [
    {
      badge: "GEPLANT",
      title: "Venue Collaborations",
      body: "Exklusive Kooperationen mit ausgewählten Zürcher Adressen.",
    },
    {
      badge: "GEPLANT",
      title: "Ticketing",
      body: "Tickets direkt in Pulse — ohne App-Hopping.",
    },
    {
      badge: "GEPLANT",
      title: "Mehr Städte",
      body: "Basel, Bern, Genf — sobald Zürich pulsiert.",
    },
    {
      badge: "GEPLANT",
      title: "Partner Insights",
      body: "Venue-Partner sehen, was die Stadt wirklich bewegt.",
    },
  ],
  finalCta: {
    headline: "Sei dabei, wenn Zürich den Puls spürt.",
    subtext: "Sichere dir deinen Platz auf der Warteliste.",
  },
};

/** Fallback copy from pulse_about_mockup_3.html */
export const DEFAULT_ABOUT_PAGE: AboutPageData = {
  themePreset: "editorial",
  brand: { logo: null },
  seo: {
    title: "Über uns · Pulse",
    description: "Warum Pulse existiert — und wohin wir gehen.",
  },
  nav: DEFAULT_NAV,
  footer: DEFAULT_FOOTER,
  manifesto: {
    eyebrow: "UNSERE GESCHICHTE",
    headlineLine1: "Nightlife verdient mehr",
    headlineLine2Prefix: "als eine",
    headlineHighlight: "Liste.",
    sub: "Pulse ist keine App, die einfach Events aneinanderreiht. Wir bauen die Art, wie eine ganze Generation ihre Nächte entdeckt, plant und erlebt — neu, von Grund auf.",
    logo: null,
    logoAlt: "Pulse",
  },
  comparison: {
    kicker: "WARUM PULSE",
    headline: "Du kennst das Problem schon.",
    intro:
      "Fünf Apps offen, zehn Tabs, am Ende landet ihr doch wieder dort, wo ihr immer landet.",
    oldTagline: "SO WAR ES BISHER",
    oldTags: [
      "17 Insta-Tabs",
      "Google-Bewertung von 2019",
      '"Ist das noch offen?"',
      "Gruppenchat-Chaos",
      "Zufallstreffer",
      "überfüllte Listen-Apps",
      '"Wohin jetzt?"',
      "abgelaufene Events",
    ],
    newTagline: "SO IST ES MIT PULSE",
    newStatement:
      "Ein Ort für alles, was heute in deiner Stadt läuft — kuratiert, aktuell, auf deinen Geschmack abgestimmt. Kein Rätselraten. Kein Chaos. Nur der nächste gute Abend.",
  },
  vision: {
    kicker: "UNSERE VISION",
    headlineLine1: "Pulse plant deinen Abend —",
    headlineLine2: "nicht du.",
    paragraphs: [
      'Kein Springen zwischen Google Maps, Instagram und veralteten Bewertungen. Keine halbe Stunde Gruppenchat für "wohin heute?". Pulse zeigt dir live, was in deiner Stadt läuft — kuratiert statt zusammengesucht.',
      "Unentschlossen? Der AI Concierge kennt deinen Geschmack und schlägt dir in Sekunden den passenden Abend vor. Das ist der Punkt: weniger Zeit fürs Entscheiden, mehr Zeit für den Abend selbst.",
      "Gestartet in Zürich, mit klarem Kurs.",
    ],
    expansionStages: ["ZÜRICH", "SCHWEIZ", "EUROPA", "WELTWEIT"],
    stageSeparator: "→",
  },
  focus: {
    kicker: "UNSER FOKUS",
    body: "Der Markt für Eventplattformen ist längst gesättigt — aber auch beliebig: Kulturevent, Flohmarkt, Tanzabend, alles auf derselben Seite. Pulse macht bewusst das Gegenteil. Eine einzige Aufgabe: dir die beste Auswahl für dein Abendprogramm zu geben — allein entschieden oder gemeinsam mit deinen Freunden.",
    mark: null,
  },
  community: {
    headline: "Das hier ist erst der Anfang.",
    body: "Pulse entsteht gerade — mit jedem Feedback, jeder Nacht und jedem neuen Venue-Partner wird die Vision klarer. Wer früh dabei ist, gestaltet mit: welche Features als Nächstes kommen, welche Städte als Nächstes dazukommen, wie sich Pulse anfühlen soll.",
    ctaLabel: "Sei von Anfang an dabei",
  },
};

export function resolveMarketingTheme(
  value: string | null | undefined,
): MarketingThemePreset {
  const v = (value ?? "").trim();
  return (MARKETING_THEME_PRESETS as readonly string[]).includes(v)
    ? (v as MarketingThemePreset)
    : "editorial";
}

export function formatWaitlistLiveCount(template: string, count: number): string {
  return template.replace(/\{count\}/g, String(count));
}
